/**
 * CustomerDropdown - Dropdown showing available customer options
 */

import { PersonOutlined } from "@mui/icons-material";

import { EVOTRUX_BLUE, EVOTRUX_BORDER_GREY } from "constants/index";

import { CUSTOMER_TYPES } from "../utils";

import type { CustomerSelectionItem } from "../types";

// Color constants for consistent theming
const COLORS = {
  primary: EVOTRUX_BLUE,
  primaryLight: "#e3f2fd",
  text: "#616161",
  textSecondary: "#6B7280",
  background: "#f5f5f5",
  backgroundLight: "#F3F4F6",
  backgroundHover: "#F9FAFB",
  disabled: "#EEEEEE",
} as const;

interface CustomerDropdownProps {
  isOpen: boolean;
  filteredCustomers: CustomerSelectionItem[];
  selectedIds: Set<string>;
  onSelectItem: (item: CustomerSelectionItem) => void;
  getCustomerKey: (item: CustomerSelectionItem) => string;
}

export function CustomerDropdown({
  isOpen,
  filteredCustomers,
  selectedIds,
  onSelectItem,
  getCustomerKey,
}: CustomerDropdownProps) {
  const getChipLabel = (customer: CustomerSelectionItem): string => {
    if (customer.type === CUSTOMER_TYPES.INTERNAL) {
      return `${customer.first_name} ${customer.last_name} (${customer.email})`;
    }
    if (customer.type === CUSTOMER_TYPES.EXTERNAL) {
      return customer.first_name && customer.last_name
        ? `${customer.first_name} ${customer.last_name} (${customer.email})`
        : customer.email;
    }
    return customer.email;
  };

  const getInitials = (customer: CustomerSelectionItem): string => {
    if (customer.type === CUSTOMER_TYPES.INTERNAL) {
      return `${customer.first_name[0]}${customer.last_name[0]}`.toUpperCase();
    }
    if (
      customer.type === CUSTOMER_TYPES.EXTERNAL &&
      customer.first_name &&
      customer.last_name
    ) {
      return `${customer.first_name[0]}${customer.last_name[0]}`.toUpperCase();
    }
    return "";
  };

  const isEvotruxUser = (customer: CustomerSelectionItem): boolean =>
    customer.type === CUSTOMER_TYPES.INTERNAL;

  // Helper functions for avatar styling
  const getAvatarBackgroundColor = (
    initials: string,
    isBlue: boolean,
  ): string => {
    if (!initials) return COLORS.disabled;
    return isBlue ? COLORS.primaryLight : COLORS.disabled;
  };

  const getAvatarTextColor = (initials: string, isBlue: boolean): string => {
    if (!initials) return COLORS.text;
    return isBlue ? COLORS.primary : COLORS.text;
  };

  if (!isOpen || filteredCustomers.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        marginTop: "4px",
        backgroundColor: "white",
        maxHeight: "300px",
        overflowY: "auto",
        boxShadow: "#959DA533 0px 8px 24px",
        border: `1px solid ${EVOTRUX_BORDER_GREY}`,
        borderRadius: "4px",
        zIndex: 50,
      }}
    >
      {filteredCustomers.map((customer) => {
        const itemId = getCustomerKey(customer);
        const isSelected = selectedIds.has(itemId);
        const isBlue = isEvotruxUser(customer);
        const initials = getInitials(customer);

        return (
          <div
            key={itemId}
            role="button"
            tabIndex={0}
            style={{
              display: "flex",
              alignItems: "start",
              gap: "12px",
              padding: "8px 16px",
              cursor: "pointer",
              transition: "background-color 0.2s",
              backgroundColor: isSelected
                ? COLORS.backgroundLight
                : "transparent",
            }}
            onMouseEnter={(e) => {
              if (!isSelected) {
                e.currentTarget.style.backgroundColor = COLORS.backgroundHover;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isSelected
                ? COLORS.backgroundLight
                : "transparent";
            }}
            onClick={() => onSelectItem(customer)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectItem(customer);
              }
            }}
          >
            {/* Avatar */}
            <div
              style={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                backgroundColor: getAvatarBackgroundColor(initials, isBlue),
                color: getAvatarTextColor(initials, isBlue),
                fontSize: "12px",
                fontWeight: 500,
                fontFamily: "Quicksand, sans-serif",
                marginTop: "2px",
              }}
            >
              {initials || (
                <PersonOutlined
                  sx={{
                    fontSize: 18,
                  }}
                />
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: "14px",
                  color: "black",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                {getChipLabel(customer)}
              </div>
              {((customer.type === CUSTOMER_TYPES.INTERNAL &&
                customer.company_name) ||
                (customer.type === CUSTOMER_TYPES.EXTERNAL &&
                  customer.company_name)) && (
                <div
                  style={{
                    fontSize: "12px",
                    color: COLORS.textSecondary,
                    fontFamily: "Quicksand, sans-serif",
                  }}
                >
                  {customer.company_name}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
